import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import Events from "./Events";
import Steps from "./Steps";
import Knowledges from "./Knowledges";
import CartCurrent from "./CartCurrent";
import CartAdd from "./CartAdd";
import CartHistory from "./CartHistory";
import IconMC from "../../../../components/IconMC";
import TooltipMC from "../../../../components/TooltipMC";
import CustomerMemoryModel from "../../../../models/CustomerMemoryModel";
import MiaRetailerModel from "../../../../models/MiaRetailerModel";
import showtoast from "../../../../../util/toast";
import {
  getCustomerName,
  isConfiguredTestCustomer,
  nextCustomersToTestAi,
  resolveActiveCustomerId
} from "../utils/testCustomers";

const csrfToken = document.querySelector('[name=csrf-token]')?.content;

const Memory = ({ newInbox = false, customerId }) => {
  const [sections, setSections] = useState({
    eventsOpened: false,
    stepsOpened: false,
    knowledgeOpened: false,
    cartOpened: false,
    addOpened: false,
    historyOpened: false
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [cartRefreshKey, setCartRefreshKey] = useState(0);
  const [isTestCustomer, setIsTestCustomer] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const { t } = useTranslation('ChatbotIA');
  const inboxCustomer = useSelector((state) => state.inboxRightReducer?.customer);
  const mainCustomer = useSelector((state) => state.mainReducer?.customer);

  const resolvedCustomerId = useMemo(
    () => resolveActiveCustomerId(customerId),
    [customerId]
  );
  const activeCustomerIdRef = useRef(resolvedCustomerId);
  activeCustomerIdRef.current = resolvedCustomerId;

  const labelCustomer = useMemo(() => (
    [inboxCustomer, mainCustomer].find(
      (customer) => resolveActiveCustomerId(customer?.id) === resolvedCustomerId
    )
  ), [inboxCustomer, mainCustomer, resolvedCustomerId]);

  const renderIconClass = (opened) => (opened ? "fa-chevron-up" : "fa-chevron-down");

  const renderIconName = (opened) => (opened ? "arrow-ios-upward-outline" : "arrow-ios-downward-outline");

  const handleSections = (section) => {
    const newSections = { ...sections };
    Object.keys(newSections).forEach((sec) => {
      if (sec === `${section}Opened`) {
        newSections[sec] = !newSections[sec];
      } else {
        newSections[sec] = false;
      }
    });
    setSections(newSections);
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleCartRefresh = () => {
    setCartRefreshKey((prev) => prev + 1);
  };

  useEffect(() => {
    setIsTestCustomer(false);
    if (!resolvedCustomerId) return undefined;

    let cancelled = false;

    MiaRetailerModel.get()
      .then((retailer) => {
        if (cancelled || activeCustomerIdRef.current !== resolvedCustomerId) return;
        setIsTestCustomer(isConfiguredTestCustomer(retailer, resolvedCustomerId));
      })
      .catch(() => {
        if (!cancelled && activeCustomerIdRef.current === resolvedCustomerId) {
          setIsTestCustomer(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedCustomerId]);

  const handleToggleTestCustomer = (event) => {
    const nextChecked = event.target.checked;
    const customerKey = resolvedCustomerId;
    if (!customerKey || toggling) return;

    setToggling(true);
    // Full-list replace. mia-config has no add/remove or version, so two agents
    // toggling different customers at once can overwrite each other's change.
    MiaRetailerModel.get()
      .then((retailer) => {
        const nextIds = nextCustomersToTestAi(retailer, customerKey, nextChecked);
        return MiaRetailerModel.update({ customers_to_test_ai: nextIds }, csrfToken);
      })
      .then(() => {
        if (activeCustomerIdRef.current !== customerKey) return;
        setIsTestCustomer(nextChecked);
        showtoast(
          nextChecked
            ? t('memory.mark_as_test_customer_success')
            : t('memory.unmark_as_test_customer_success')
        );
      })
      .catch((error) => {
        showtoast(error?.message || t('memory.mark_as_test_customer_error'));
      })
      .finally(() => {
        setToggling(false);
      });
  };

  const handleResetMemory = () => {
    if (!resolvedCustomerId || resetting || !isTestCustomer) return;

    const customerName = getCustomerName(labelCustomer) || String(resolvedCustomerId);

    if (!window.confirm(t('memory.reset_confirm', {
      customerId: resolvedCustomerId,
      customerName
    }))) return;

    setResetting(true);
    CustomerMemoryModel.resetMemory(resolvedCustomerId)
      .then(() => {
        showtoast(t('memory.reset_success'));
        handleRefresh();
      })
      .catch((error) => {
        showtoast(error?.message || t('memory.reset_error'));
      })
      .finally(() => {
        setResetting(false);
      });
  };

  const content = (
    <>
      <div className="align-items-center d-flex flex-nowrap justify-content-between">
        <span className="font-600 fz-14 lh-24 py-16 text-gray-950">{t('memory.title')}</span>
        <div className="align-items-center d-flex flex-nowrap tw-wrapper">
          {isTestCustomer && (
            <TooltipMC text={t('memory.reset')} position="bottom">
              <button
                type="button"
                className="tw-flex tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-red-500 tw-mr-2 disabled:tw-opacity-50"
                onClick={handleResetMemory}
                disabled={resetting}
                aria-label={t('memory.reset')}
              >
                <i className="mi-trash-2-outline fs-16" aria-hidden="true" />
              </button>
            </TooltipMC>
          )}
          <i className="mi-refresh-fill fs-16 cursor-pointer" onClick={handleRefresh} aria-hidden="true"></i>
        </div>
      </div>

      {resolvedCustomerId && (
        <label className="align-items-center d-flex fz-12 text-gray-950 pb-12 mb-0 cursor-pointer">
          <input
            type="checkbox"
            className="mr-8"
            checked={isTestCustomer}
            disabled={toggling}
            onChange={handleToggleTestCustomer}
          />
          {t('memory.mark_as_test_customer')}
        </label>
      )}

      <div className="divider-line p-relative"></div>

      <div className="align-items-center cursor-pointer d-flex justify-content-between" onClick={() => handleSections("events")}>
        <span className="fz-14 lh-24 py-16 text-gray-950">{t('memory.events')}</span>
        {newInbox ? <IconMC name={renderIconName(sections.eventsOpened)} size="base" /> : <i className={`fa ${renderIconClass(sections.eventsOpened)} fz-16`} aria-hidden="true"></i>}
      </div>
      {sections.eventsOpened && (
        <Events refreshKey={refreshKey} customerId={resolvedCustomerId} />
      )}

      <div className="divider-line p-relative"></div>

      <div className="align-items-center cursor-pointer d-flex justify-content-between" onClick={() => handleSections("steps")}>
        <span className="fz-14 lh-24 py-16 text-gray-950">{t('memory.steps')}</span>
        {newInbox ? <IconMC name={renderIconName(sections.stepsOpened)} size="base" /> : <i className={`fa ${renderIconClass(sections.stepsOpened)} fz-16`} aria-hidden="true"></i>}
      </div>
      {sections.stepsOpened && (
        <Steps refreshKey={refreshKey} customerId={resolvedCustomerId} />
      )}

      <div className="divider-line p-relative"></div>

      <div className="align-items-center cursor-pointer d-flex justify-content-between" onClick={() => handleSections("knowledge")}>
        <span className="fz-14 lh-24 py-16 text-gray-950">{t('memory.knowledges')}</span>
        {newInbox ? <IconMC name={renderIconName(sections.knowledgeOpened)} size="base" /> : <i className={`fa ${renderIconClass(sections.knowledgeOpened)} fz-16`} aria-hidden="true"></i>}
      </div>
      {sections.knowledgeOpened && (
        <Knowledges refreshKey={refreshKey} customerId={resolvedCustomerId} />
      )}

      <div className="align-items-center d-flex header justify-content-between">
        <span className="font-600 fz-14 lh-24 py-16 text-gray-950">{t('cart.inbox.title')}</span>
        <i className="mi-refresh-fill fs-16 cursor-pointer" onClick={handleCartRefresh} aria-hidden="true"></i>
      </div>

      <div className="divider-line p-relative"></div>

      <div className="align-items-center cursor-pointer d-flex justify-content-between" onClick={() => handleSections("cart")}>
        <span className="fz-14 lh-24 py-16 text-gray-950">{t('cart.inbox.current')}</span>
        {newInbox ? <IconMC name={renderIconName(sections.cartOpened)} size="base" /> : <i className={`fa ${renderIconClass(sections.cartOpened)} fz-16`} aria-hidden="true"></i>}
      </div>
      {sections.cartOpened && (
        <CartCurrent refreshKey={cartRefreshKey} customerId={customerId} />
      )}

      <div className="divider-line p-relative"></div>

      <div className="align-items-center cursor-pointer d-flex justify-content-between" onClick={() => handleSections("add")}>
        <span className="fz-14 lh-24 py-16 text-gray-950">{t('cart.playground.add_product')}</span>
        {newInbox ? <IconMC name={renderIconName(sections.addOpened)} size="base" /> : <i className={`fa ${renderIconClass(sections.addOpened)} fz-16`} aria-hidden="true"></i>}
      </div>
      {sections.addOpened && (
        <CartAdd customerId={customerId} onAdded={handleCartRefresh} />
      )}

      <div className="divider-line p-relative"></div>

      <div className="align-items-center cursor-pointer d-flex justify-content-between" onClick={() => handleSections("history")}>
        <span className="fz-14 lh-24 py-16 text-gray-950">{t('cart.inbox.history')}</span>
        {newInbox ? <IconMC name={renderIconName(sections.historyOpened)} size="base" /> : <i className={`fa ${renderIconClass(sections.historyOpened)} fz-16`} aria-hidden="true"></i>}
      </div>
      {sections.historyOpened && (
        <CartHistory refreshKey={cartRefreshKey} customerId={customerId} />
      )}
    </>
  );

  if (newInbox) {
    return (
      <div className="tw-flex tw-flex-col tw-h-[calc(100vh-250px)] tw-max-h-screen tw-min-h-[400px]">
        <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-pr-1">
          {content}
        </div>
      </div>
    );
  }

  return content;
};

export default Memory;
